import { LayoutCluster } from "../Topology/LayoutCluster";
import { List } from "../Utils/List";
import { Point, Rectangle, Matrix } from "../Utils/Matrix";
import { TopoNode } from "../Topology/TopoNode";
import { StringUtils } from "../Utils/StringUtils";
import { TopoMap } from "../Topology/TopoMap";
import { SpanningTree } from "../Topology/SpanningTree";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class RadialLayoutCluster extends LayoutCluster<RadialLayoutCluster>
{
	constructor(root: TopoNode) {
		super(root);
	}

	public Place(distance: number, angle: number): void {
		this.Root.X = 0;
		this.Root.Y = 0;

		var leftMost: Point = this.LeftMostLocation();

		if (leftMost.X < this.Root.X)
			distance += this.Root.X - leftMost.X;

		this.Offset(distance, 0);
		this.Rotate(angle);
	}
}

//export class RadialLayoutSettingsJson {
//    min_radius: string;
//    max_radius: string;
//    max_angle: string;
//    maximize_root: string;
//}

export class RadialLayoutSettings implements ObjectSerializer {
	private static DefaultMinRadius = 200;
	private static DefaultMaxRadius = 3000;
	private static DefaultMaxAngle = 360;

	constructor() {
		this.MinRadius = RadialLayoutSettings.DefaultMinRadius;
		this.MaxRadius = RadialLayoutSettings.DefaultMaxRadius;
		this.MaxAngle = RadialLayoutSettings.DefaultMaxAngle;
		this.LayoutAngle = 0;
		this.MaximizeRoot = true;
	}

	public MinRadius: number;
	public MaxRadius: number;
	public MaxAngle: number;
	public MaximizeRoot: boolean;

	// Not persisted
	public LayoutAngle: number;

	public Clone(): RadialLayoutSettings {
		var result = new RadialLayoutSettings();

		result.MinRadius = this.MinRadius;
		result.MaxRadius = this.MaxRadius;
		result.MaxAngle = this.MaxAngle;
		result.MaximizeRoot = this.MaximizeRoot;
		result.LayoutAngle = this.LayoutAngle;

		return result;
    }

    //public static FromJson(json: RadialLayoutSettingsJson): RadialLayoutSettings {
    //    var result = new RadialLayoutSettings();

    //    if (json.min_radius !== undefined)
    //        result.MinRadius = parseInt(json.min_radius);

    //    if (json.max_radius !== undefined)
    //        result.MaxRadius = parseInt(json.max_radius);

    //    if (json.max_angle !== undefined)
    //        result.MaxAngle = parseInt(json.max_angle);

    //    if (json.maximize_root !== undefined)
    //        result.MaximizeRoot = StringUtils.ParseBool(json.maximize_root);

    //    return result;
    //}

	public Write(obj: any, context: any): void {
		obj.radial_layout_settings = {};
		obj = obj.radial_layout_settings;

		obj.min_radius = ValueEncoder.EncodeInteger(this.MinRadius);
		obj.max_radius = ValueEncoder.EncodeInteger(this.MaxRadius);
		obj.max_angle = ValueEncoder.EncodeInteger(this.MaxAngle);
		obj.maximize_root = ValueEncoder.EncodeBoolean(this.MaximizeRoot);
	}

	public Read(obj: any, context: any): void {
		if (!obj.radial_layout_settings)
			throw new Error();
		obj = obj.radial_layout_settings;

		if (obj.min_radius !== undefined)
			this.MinRadius = ValueEncoder.DecodeInteger(obj.min_radius);
		if (obj.max_radius !== undefined)
			this.MaxRadius = ValueEncoder.DecodeInteger(obj.max_radius);
		if (obj.max_angle !== undefined)
			this.MaxAngle = ValueEncoder.DecodeInteger(obj.max_angle);
		if (obj.maximize_root !== undefined)
			this.MaximizeRoot = ValueEncoder.DecodeBoolean(obj.maximize_root);
	}

}

export class RadialLayout {
	public static DefaultTreeMargin = 100;

	private static NodeMargin = 20;
	private static ThreeSixty = 360;
	private static MinNodesPerRow = 25;
	private static LayerMargin = 100;

	public MinRadius: number;
	public MaxRadius: number;
	public MaxAngle: number;
	public TreeMargin: number;
	public MaximizeRoot: boolean;
	public LayoutAngle: number;

	private _topoMap: TopoMap;

	constructor(topoMap: TopoMap) {
		this._topoMap = topoMap;
		this.Settings = topoMap.LayoutSettings.RadialSettings;
		this.TreeMargin = RadialLayout.DefaultTreeMargin;
	}

	public set Settings(value: RadialLayoutSettings) {
		if (value != null) {
			this.MinRadius = value.MinRadius;
			this.MaxRadius = value.MaxRadius;
			this.MaxAngle = value.MaxAngle;
			this.MaximizeRoot = value.MaximizeRoot;
			this.LayoutAngle = value.LayoutAngle;

			if (this.MaxAngle <= 0)
				this.MaxAngle = 1;

			if (this.MaxRadius <= 0)
				this.MaxRadius = 1;

			if (this.MinRadius <= 0)
				this.MinRadius = 1;
		}
	}

	public LayoutSpanningTrees(spanningTrees: List<SpanningTree>): void {
		spanningTrees.ForEach((tree) => {
			tree.Nodes.ForEach((node) => {
				node.Location = new Point(0, 0);
			});

			this.LayoutNodes(tree.Root, tree);
		});
		
		// Offset the trees
		if (spanningTrees.Count > 0) {
			var firstTree: SpanningTree = spanningTrees.Get(0);
			firstTree.CalculateSize();
			var leftX = firstTree.Right;

			for (var i = 1; i < spanningTrees.Count; i++) {
				var tree: SpanningTree = spanningTrees.Get(i);
				tree.CalculateSize();
				var xOffset = leftX - tree.Left + this.TreeMargin;
				leftX += tree.Width + this.TreeMargin;

				tree.Offset(xOffset, 0);
			}
		}
	}

	public LayoutChildren(node: TopoNode): RadialLayoutCluster {
		var cluster: RadialLayoutCluster = null;

		var tree: SpanningTree = this._topoMap.OwningTree(node);
		if (tree != null) {
			var nodeLocation: Point = node.Location;

			this._topoMap.DescendantNodes(node).ForEach((d) => {
				d.Location = new Point(0, 0);
			});

			cluster = this.LayoutNodes(node, tree);
			node.Offset(-nodeLocation.X, -nodeLocation.Y);

			cluster.Rotate(-this.LayoutAngle);
			cluster.Offset(nodeLocation.X, nodeLocation.Y);
		}

		return cluster;
	}

	public LayoutNodes(root: TopoNode, tree: SpanningTree): RadialLayoutCluster {
		var currentCluster = new RadialLayoutCluster(root);

		tree.ChildNodes(root).ForEach((child) => {
			currentCluster.AddCluster(this.LayoutNodes(child, tree));
		});

		var clusters: List<RadialLayoutCluster> = currentCluster.Clusters;
		if (clusters.Count > 0) {
			var requiredArcLength: number = this.RequiredArcLength(clusters);
			var requiredAngle: number = this.RequiredAngle(requiredArcLength);

			if (root == tree.Root && this.MaximizeRoot)
				requiredAngle = RadialLayout.ThreeSixty;

			var requiredRadius: number = this.RequiredRadius(requiredArcLength, requiredAngle);

			if (requiredRadius > this.MaxRadius) {
				var singles = new List<RadialLayoutCluster>();
				var multiples = new List<RadialLayoutCluster>();

				clusters.ForEach((cluster) => {
					if (cluster.Clusters.Count == 0)
						singles.Add(cluster);
					else
						multiples.Add(cluster);
				});

				var singlesArcLength: number = this.RequiredArcLength(singles);
				var multiplesArcLength: number = this.RequiredArcLength(multiples);
				var singlesAngle: number = this.RequiredAngle(singlesArcLength);
				var multiplesAngle: number = this.RequiredAngle(multiplesArcLength);

				var angle: number = Math.max(multiplesAngle, singlesAngle);

				if (root == tree.Root)
					angle = RadialLayout.ThreeSixty;

				var radius: number = this.MinRadius;

				if (singles.Count > 0) {
					var allowLayering = false;
					radius = this.RequiredRadius(singlesArcLength, angle);
					if (radius > this.MaxRadius) {
						radius = this.MaxRadius;
						allowLayering = true;
					}

					radius = this.LayoutNodesAndClusters(root, tree, singles, singlesArcLength, angle, radius, allowLayering);
					radius += RadialLayout.LayerMargin;
				}

				if (multiples.Count > 0) {
					this.OrderClusters(multiples);
					this.LayoutNodesAndClusters(root, tree, multiples, multiplesArcLength, angle, radius);
				}
			}
			else {
				this.OrderClusters(clusters);
				this.LayoutNodesAndClusters(root, tree, clusters, requiredArcLength, requiredAngle, this.MinRadius);
			}
		}

		// Now that it is  laid out we can calculate its size
		// so that is is accurate for the next iteration
		currentCluster.CalculateSize();
		return currentCluster;
	}

	private LayoutNodesAndClusters(root: TopoNode, tree: SpanningTree, clusters: List<RadialLayoutCluster>, arcLength: number, sectorAngle: number,
		minRadius: number, allowLayering: boolean = false): number {

		// If layering is allowed the required angle should be allowed to come back larger than the
		// allowed angle 
		var requiredAngle: number = this.RequiredAngle(arcLength, minRadius, sectorAngle, allowLayering ? false : true);

		var startDegree: number = RadialLayout.ThreeSixty - sectorAngle / 2;

		// Region margin allows the layout to use the provided sector when
		// more than is required is allocated.
		var regionAngleMargin = 0;
		if (requiredAngle < sectorAngle)
			regionAngleMargin = (sectorAngle - requiredAngle) / clusters.Count;

		var currentDegree = (startDegree + regionAngleMargin / 2) % RadialLayout.ThreeSixty;
		var angleCovered = 0;
		for (var i = 0; i < clusters.Count; i++) {
			var node: RadialLayoutCluster = clusters.Get(i);

			// Region angle is the angle of the sector allocated based on
			// the percentage of the arclength this node gets
			var regionAngle = ((requiredAngle) * ((node.Height) / arcLength));
			var regionStart = currentDegree;

			var nodeAngle = (regionStart + ((regionAngle) * (Math.abs(node.Top) / (node.Height == 0 ? 1 : node.Height)))) % RadialLayout.ThreeSixty;

			if (nodeAngle < startDegree && nodeAngle > (startDegree + sectorAngle) % RadialLayout.ThreeSixty)
				nodeAngle = (startDegree + (nodeAngle + RadialLayout.ThreeSixty - (startDegree + sectorAngle))) % RadialLayout.ThreeSixty;

			var radius = this.RequiredRadius(node.Height, regionAngle + regionAngleMargin, minRadius);

			if (allowLayering)
				radius += RadialLayout.LayerMargin * (angleCovered / sectorAngle);

			// Removing this makes the map look more radial
			//if (i % 2 == 0)
			//	radius += LayerMargin/2;

			node.Place(radius, nodeAngle);

			// Increment to next region
			angleCovered += regionAngle + regionAngleMargin;
			currentDegree = (currentDegree + regionAngle + regionAngleMargin) % RadialLayout.ThreeSixty;
			if (currentDegree < startDegree && currentDegree > (startDegree + sectorAngle) % RadialLayout.ThreeSixty)
				currentDegree = (startDegree + sectorAngle - currentDegree) % RadialLayout.ThreeSixty;
		}

		return this.RequiredRadius(arcLength, requiredAngle, minRadius);
	}

	/// <summary>
	/// Clusters are orderered by width in Tapered order with the
	/// widest cluster in the center.
	/// The ordering allows the cluster currently being layed out to 
	/// be shaped like a tear drop with the longest cluster pointing out
	/// at 0 degrees.  Layout starts with the first cluster at the start of
	/// the sector angle and finishes at the other end with the last
	/// </summary>
	/// <param name="clusters">The clusters to order</param>
	private OrderClusters(clusters: List<RadialLayoutCluster>): void {
		clusters.Values.sort((c1, c2) => c1.Width == c2.Width ? 0 : c1.Width > c2.Width ? 1 : -1);

		for (var i = 1; i < clusters.Count / 2; i++) {
			var move: RadialLayoutCluster = clusters.Values[i];
			clusters.RemoveAt(i);
			clusters.Values.splice(clusters.Count + 1 - i, 0, move);
		}
	}

	/// <summary>
	/// ArcLength is calculated by summing the cluster
	/// heights.  Since clusters are laid out with with the width
	/// parallel to the radius the height becomes the arc length.
	/// </summary>
	/// <param name="clusters"></param>
	/// <returns></returns>
	private RequiredArcLength(clusters: List<RadialLayoutCluster>): number {
		var arcLength = 0;
		clusters.ForEach((cluster) => {
			arcLength += cluster.Height;
		});

		return arcLength;
	}

	/// <summary>
	/// The required radius is calculated Based on the required arc length and sector angle.
	/// </summary>
	/// <param name="requiredArcLength"></param>
	/// <param name="angle"></param>
	/// <param name="minRadius"></param>
	/// <returns></returns>
	private RequiredRadius(requiredArcLength: number, angle: number, minRadius: number = null): number {
		if (!minRadius) minRadius = this.MinRadius;

		var result = (requiredArcLength * RadialLayout.ThreeSixty) / (angle * 2 * Math.PI);

		if (result < minRadius)
			result = minRadius;

		return result;
	}
		
	/// <summary>
	/// Determines the minimum angle required to fit the sector of the given
	/// arc length with the provided radius.  If restrict angle is true the 
	/// angle will not exceed MaxAngle.  If restrict angle is false the angle could
	/// exceed maxAngle, useful for laying out a sector level on more than one row.
	/// </summary>
	/// <param name="requiredArcLength"></param>
	/// <param name="radius"></param>
	/// <param name="restrictAngle">If restrict angle is true the result will not exceed MaxAngle</param>
	/// <returns></returns>
	private RequiredAngle(requiredArcLength: number, radius: number = null, maxAngle: number = null, restrictAngle: boolean = true): number {
		if (!radius) radius = this.MinRadius;
		if (!maxAngle) maxAngle = this.MaxAngle;
		if (!restrictAngle) restrictAngle = true;
		var angle = (requiredArcLength * RadialLayout.ThreeSixty) / (2 * Math.PI * radius);

		if (restrictAngle && angle > maxAngle)
			angle = maxAngle;

		return angle;
	}
}
